import { AbstractControl, FormBuilder, FormGroup } from "@angular/forms";
import { Observable } from "rxjs";
import { ProductCollectionGroupsEnum } from "../../../../../shared/enums/product-collection-groups-enum";
import { ProductCollectionsEnum } from "../../../../../shared/enums/product-collections.enum";
import { ObservableBaseForm } from "../../../../../shared/forms/observable-base.form";
import { map } from "rxjs/operators";
import { merge } from "rxjs/internal/observable/merge";
import { AggregationGroupModel } from "../../../../../shared/models/search/aggregations/aggregation-group.model";
import { ProductsParamsFilter } from "../../../../models/search-params/filters/products-params-filter";
import { ProductsResultsFilter } from "../../../../models/search-results/filters/products-results-filter";
import { ProductsFilterFormService } from "../services/products-filter-form.service";

export class ProductsFilterForm extends ObservableBaseForm<ProductsParamsFilter> {
    public get collectionGroup(): AbstractControl {
        return this.formInstance.get("collectionGroup");
    }

    public get collection(): AbstractControl {
        return this.formInstance.get("collection");
    }

    public get facets(): FormGroup {
        return this.formInstance.get("facets") as FormGroup;
    }

    public get sorting(): AbstractControl {
        return this.formInstance.get("sorting");
    }

    public static createForm(formBuilder: FormBuilder, initialValues: ProductsParamsFilter,
                             filterService: ProductsFilterFormService): ProductsFilterForm {

        const form = formBuilder.group({
            collectionGroup: formBuilder.control(initialValues.collectionGroup),
            collection: formBuilder.control(initialValues.collection),
            facets: formBuilder.group(Object.keys(initialValues.facets).reduce((group, alias) => {
                group[alias] = formBuilder.control(initialValues.facets[alias]);
                return group;
            }, {})),
            sorting: formBuilder.control(initialValues.sorting)
        });

        return new ProductsFilterForm(form, initialValues, formBuilder, filterService);
    }

    public constructor(
        formInstance: FormGroup,
        initialValues: ProductsParamsFilter,
        private formBuilder: FormBuilder,
        private filterService: ProductsFilterFormService
    ) {
        super(formInstance, initialValues);
        merge(
            this.changeObservable,
            this.submitObservable
        )
        .subscribe(model => {
            // sync form & params state
            this.formInstance.patchValue(model, {emitEvent: false});
            this.initialValues = model;
        });
    }

    public reset(): void {
        this.removeFacetControls();
        this.formInstance.patchValue({
            collectionGroup: this.filterService.getDefaultCollectionGroup(),
            collection: this.filterService.getDefaultCollection(),
            facets: {},
            sorting: this.filterService.getDefaultSortingOption()
        }, {emitEvent: false});
    }

    public buildFormControls(paramsFilter: ProductsParamsFilter, resultsFilter: ProductsResultsFilter): void {
        this.removeChangeListener();

        this.collectionGroup.setValue(paramsFilter.collectionGroup);
        this.collection.setValue(paramsFilter.collection);
        this.sorting.setValue(paramsFilter.sorting);
        this.buildFacetControls(resultsFilter.facets || []);

        this.addChangeListener();
    }

    private removeFacetControls = () => {
        const facets = Object.keys(this.facets.controls);
        for (const alias of facets) {
            this.facets.removeControl(alias);
        }
    }

    private buildFacetControls(aggregations: Array<AggregationGroupModel>): void {
        Object.keys(this.facets.controls).forEach(facetAlias => {
            this.facets.removeControl(facetAlias);
        });
        aggregations.map(x => x.alias).forEach(facetAlias => {
            const value = this.initialValues.facets[facetAlias] || [];
            this.facets.addControl(facetAlias, this.formBuilder.control(value));
        });
    }

    protected createChangeSubscription(): Observable<ProductsParamsFilter> {
        return merge(
            this.collectionGroup.valueChanges.pipe(
                map((collectionGroup: ProductCollectionGroupsEnum) => ({
                        collectionGroup: collectionGroup,
                        collection: this.filterService.isSingleCollectionGroup(collectionGroup)
                            ? this.filterService.getCollectionForSingleCollectionGroup(collectionGroup)
                            : this.filterService.getDefaultCollection(),
                        facets: {}
                    })
                )
            ),
            this.collection.valueChanges.pipe(
                map((collection: ProductCollectionsEnum) => ({
                        collection: collection,
                        facets: {}
                    })
                )
            ),
            this.facets.valueChanges.pipe(
                map((facets: Array<string>) => ({facets: facets}))
            ),
            this.sorting.valueChanges.pipe(
                map((sorting: string) => ({sorting}))
            )
        )
        .pipe(
            map(partialModel => this.getModel(partialModel))
        );
    }
}
